import { useState, useMemo, useEffect } from "react";
import { Search } from "lucide-react";
import emptyFolderIcon from '@/assets/empty-folder-icon.svg';
import { apiService } from '@/services/core/api.service';
import FilterSelect from '@/shared/components/FilterSelect';
import { Badge } from "@/shared/components/Badge";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ApiProject {
  id: string;
  name: string;
  description?: string;
}

interface Proyecto {
  id: string;
  nombre: string;
  responsable: string;
  descripcion: string;
  estado: "Activo" | "Inactivo";
}

function mapProject(p: ApiProject): Proyecto {
  return {
    id: p.id,
    nombre: p.name,
    responsable: "Sin responsable",
    descripcion: p.description ?? "Sin descripción",
    estado: "Activo",
  };
}

// ─── Dropdown helper ──────────────────────────────────────────────────────────

// ─── Empty states ─────────────────────────────────────────────────────────────

const NoDataState = () => (
  <div className="flex flex-col items-center justify-center py-20 gap-3">
    <img src={emptyFolderIcon} alt="Sin proyectos" className="w-40 h-auto mb-2" />
    <p className="text-base font-semibold text-gray-700">Aún no hay datos para mostrar</p>
    <p className="text-sm text-gray-400">Los proyectos aparecerán aquí una vez que se registren.</p>
  </div>
);

const NoResultsState = () => (
  <div className="flex flex-col items-center justify-center py-16 gap-2">
    <p className="text-sm font-medium text-gray-500">Sin resultados</p>
    <p className="text-sm text-gray-400">Intenta con otros filtros.</p>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const ProyectosAlmacen = () => {
  const [proyectos, setProyectos] = useState<Proyecto[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  const [search, setSearch]                   = useState("");
  const [filterProyecto, setFilterProyecto]   = useState("");
  const [filterResponsable, setFilterResponsable] = useState("");
  const [filterEstado, setFilterEstado]       = useState("");

  useEffect(() => {
    setLoading(true);
    apiService
      .request<ApiProject[]>("/projects")
      .then((data) => setProyectos(data.map(mapProject)))
      .catch(() => setError("No se pudieron cargar los proyectos"))
      .finally(() => setLoading(false));
  }, []);

  const handleLimpiar = () => {
    setSearch("");
    setFilterProyecto("");
    setFilterResponsable("");
    setFilterEstado("");
  };

  const uniqueProyectos   = useMemo(() => [...new Set(proyectos.map((p) => p.nombre))], [proyectos]);
  const uniqueResponsables = useMemo(() => [...new Set(proyectos.map((p) => p.responsable).filter((r) => r !== "Sin responsable"))], [proyectos]);

  const rows = useMemo(() => {
    return proyectos.filter((p) => {
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        p.nombre.toLowerCase().includes(q) ||
        p.responsable.toLowerCase().includes(q) ||
        p.descripcion.toLowerCase().includes(q);
      const matchProyecto    = !filterProyecto    || p.nombre      === filterProyecto;
      const matchResponsable = !filterResponsable || p.responsable === filterResponsable;
      const matchEstado      = !filterEstado      || p.estado      === filterEstado;
      return matchSearch && matchProyecto && matchResponsable && matchEstado;
    });
  }, [proyectos, search, filterProyecto, filterResponsable, filterEstado]);

  if (loading) {
    return (
      <div className="mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Proyectos</h1>
          <p className="text-sm text-gray-500 mt-1">Revisa aquí la lista de proyectos activos</p>
        </div>
        <p className="text-sm text-gray-400">Cargando proyectos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Proyectos</h1>
        </div>
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Proyectos</h1>
        <p className="text-sm text-gray-500 mt-1">Revisa aquí la lista de proyectos activos</p>
      </div>

      {proyectos.length === 0 ? (
        <NoDataState />
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap justify-start items-center gap-3 mb-6">
            {/* Search */}
            <div className="relative w-[280px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar proyecto..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998] focus:border-transparent"
              />
            </div>

            <FilterSelect
              label="Proyecto"
              options={(uniqueProyectos).map((o) => ({ value: o, label: o }))}
              value={filterProyecto}
              onChange={setFilterProyecto}
            ancho="contenido"
          />
            {uniqueResponsables.length > 0 && (
              <FilterSelect
                label="Responsable"
                options={(uniqueResponsables).map((o) => ({ value: o, label: o }))}
                value={filterResponsable}
                onChange={setFilterResponsable}
              ancho="contenido"
          />
            )}
            <FilterSelect
              label="Estado"
              options={(["Activo", "Inactivo"]).map((o) => ({ value: o, label: o }))}
              value={filterEstado}
              onChange={setFilterEstado}
            ancho="contenido"
          />

            <button
              onClick={handleLimpiar}
              className="text-sm font-semibold text-[#315FB9] hover:underline"
            >
              Limpiar
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr style={{ backgroundColor: "#FAF9FF", borderBottom: "1px solid #E4E4E4" }}>
                    {["Proyecto", "Responsable", "Descripción", "Estado"].map((h) => (
                      <th
                        key={h}
                        className="text-left py-3 px-4 text-sm font-semibold first:pl-6 last:pr-6"
                        style={{ color: "#534D59" }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={4}><NoResultsState /></td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr
                        key={row.id}
                        className="hover:bg-gray-50/50 transition-colors"
                        style={{ borderBottom: "1px solid #E4E4E4" }}
                      >
                        <td className="py-4 px-4 pl-6 text-gray-700 font-medium whitespace-nowrap">
                          {row.nombre}
                        </td>
                        <td className="py-4 px-4 text-gray-600 whitespace-nowrap">
                          {row.responsable}
                        </td>
                        <td className="py-4 px-4 text-gray-500 max-w-sm">
                          {row.descripcion}
                        </td>
                        <td className="py-4 px-4 pr-6 whitespace-nowrap">
                          <Badge color={row.estado === "Activo" ? "verde" : "gris"}>
                            {row.estado}
                          </Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProyectosAlmacen;
